import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Check } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/4965823/pexels-photo-4965823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        backgroundColor: 'rgba(0,0,0,0.5)',
        backgroundBlendMode: 'darken'
      }}
    >
      <div className="bg-white p-8 rounded-sm shadow-sm w-full max-w-md border" style={{ borderColor: '#E5E1DB' }}>
        <div className="flex items-center justify-center mb-6">
          <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-10 h-10 object-contain mr-3" />
          <div>
            <h1 className="text-xl font-light tracking-wide" style={{ color: '#2C1A0E' }}>Maluti Holdings</h1>
            <p className="text-xs tracking-wide" style={{ color: '#B8860B' }}>Operations Suite</p>
          </div>
        </div>

        <h2 className="text-lg font-medium mb-5 text-center" style={{ color: '#8B7355' }}>Sign in to your account</h2>

        {error && (
          <div className="mb-4 p-3 rounded-sm text-sm" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }} data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="login-form">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB' }}
              required
              data-testid="login-email-input"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-sm focus:outline-none focus:ring-2 transition-all duration-200"
              style={{ borderColor: '#E5E1DB' }}
              required
              data-testid="login-password-input"
            />
          </div>

          <label className="flex items-center gap-3 mb-6 cursor-pointer" data-testid="remember-me-checkbox">
            <div className="relative">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="sr-only"
              />
              <div
                className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200"
                style={{
                  borderColor: rememberMe ? '#D4AF37' : '#CBD5E1',
                  backgroundColor: rememberMe ? '#D4AF37' : 'transparent'
                }}
              >
                {rememberMe && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
            <span className="text-sm" style={{ color: '#4A4033' }}>Keep me signed in (30 days)</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-sm font-medium transition-all duration-200 disabled:opacity-50 hover:shadow-md"
            style={{ backgroundColor: '#D4AF37', color: 'white' }}
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm" style={{ color: '#8B7355' }}>
          Don't have an account?{' '}
          <Link to="/register" className="font-medium underline" style={{ color: '#D4AF37' }} data-testid="register-link">
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
